/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { UUID } from "crypto";
import { DocumentResponse } from "@interfaces/response";
import { ApiError } from "@interfaces/response";
import { DOCUMENTS_CATALOG } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import {
  useLazyGetBankAccountsSecondGuaranteeQuery,
  useLazyGetDocumentAppIdSecondGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  usePostBankAccountsSecondGuaranteeMutation,
  usePatchBankAccountSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { useLazyGetBanksByCodeSecondGuaranteeQuery } from "@api/second_guarantee_api/catalogsSecondGuarantee";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { BankInformationPayload, DocumentBank } from "@interfaces/SecondGuarantee";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import React from "react";

interface BankAccountForm {
  id?: string;
  clabe: string;
  bank_id: string;
  bank_name: string;
  documents: DocumentBank[];
}

const InformacionBancariaSegundoAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();

  const shareholderData = useSelector((state: RootState) => state.secondGuarante.guarantee);

  const [listDocuments, setListDocuments] = useState<DocumentResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [numberOfBankStatements, setNumberOfBankStatements] = useState<number>(1);
  const emptyBankAccount: BankAccountForm = {
    id: "",
    clabe: "",
    bank_id: "",
    bank_name: "",
    documents: Array.from({ length: numberOfBankStatements }, (_, index) => ({
      document_id: "",
      document_order: index + 1,
    })),
  };

  const handleOnClickRestart = () => {
    navigate("/avales/expediente_digital_segundo_aval");
  };

  const [triggerGetBanks] = useLazyGetBanksByCodeSecondGuaranteeQuery();
  const [getBankAccounts] = useLazyGetBankAccountsSecondGuaranteeQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdSecondGuaranteeQuery();
  const [patchPersonData] = usePatchPersonSecondGuaranteeMutation();

  const [triggerPatchBankAccount] = usePatchBankAccountSecondGuaranteeMutation();
  const [triggerPostBankAccount] = usePostBankAccountsSecondGuaranteeMutation();

  const formik = useFormik({
    initialValues: {
      accounts: [{ ...emptyBankAccount }],
    },
    validationSchema: yup.object().shape({
      accounts: yup.array().of(
        yup.object().shape({
          clabe: yup
            .string()
            .min(18, "Formato incorrecto, debe tener 18 dígitos")
            .required("Campo requerido.")
            .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
              const num_results: number = formik.values.accounts.filter((item: any) => item.clabe === value).length;
              return num_results > 1 ? false : true;
            }),
          bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
          documents: yup
            .array()
            .of(
              yup.object().shape({
                document_id: yup.string().required("Documento requerido."),
                document_order: yup.number().required().min(1).max(numberOfBankStatements),
              }),
            )
            .min(1, "Debes cargar al menos un documento."),
        }),
      ),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        for (let index = 0; index < values.accounts.length; index++) {
          const account = values.accounts[index];

          const payload: BankInformationPayload = {
            loan_application_id: String(shareholderData.loan_application_id),
            client_details_id: undefined,
            person_id: String(shareholderData?.id),
            account: 0,
            account_holder: "",
            clabe: account.clabe,
            bank_id: account.bank_id as UUID,
            documents: account.documents
              .filter((doc) => doc.document_id)
              .map((doc, i) => ({
                document_id: doc.document_id,
                document_order: i + 1,
              })),
          };

          if (account.id) {
            await triggerPatchBankAccount({
              solicitudId: String(shareholderData.loan_application_id),
              bankAccountId: account.id,
              body: payload,
            }).unwrap();
          } else {
            await triggerPostBankAccount({
              solicitudId: String(shareholderData.loan_application_id),
              body: payload,
            }).unwrap();
          }
        }

        await patchPersonData({
          personId: String(shareholderData?.id),
          body: {
            bank_references_status: "COMPLETO",
          },
        }).unwrap();

        handleOnClickRestart();
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const getBankNameByCode = useCallback(
    async (bank_code: string) => {
      try {
        const bank_info = await triggerGetBanks(bank_code).unwrap();
        if (bank_info) {
          return {
            id: bank_info.id,
            name: bank_info.name,
          };
        } else {
          return null;
        }
      } catch (error) {
        console.error("Error al obtener el banco:", error);
        return null;
      }
    },
    [listDocuments],
  );

  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = event.target;
    const only_numbers = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, only_numbers);

    const bank_code = only_numbers.substring(0, 3);
    const bank_info = bank_code.length === 3 ? await getBankNameByCode(bank_code) : null;
    if (bank_info) {
      formik.setFieldValue(`accounts[${index}].bank_id`, bank_info?.id || "");
      formik.setFieldValue(`accounts[${index}].bank_name`, bank_info?.name || "");
    } else {
      formik.setFieldValue(`accounts[${index}].bank_id`, "");
      formik.setFieldValue(`accounts[${index}].bank_name`, "");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const list_bank_accounts = await getBankAccounts(String(shareholderData.loan_application_id)).unwrap();
        const bank_accounts = list_bank_accounts.filter((item) => item.person_id === String(shareholderData?.id));
        const documents = await getDocumentsByAppId({
          loanApplicationId: String(shareholderData.loan_application_id),
          personId: String(shareholderData?.id),
          status: true,
        }).unwrap();

        const accounts: BankAccountForm[] = [];

        for (let index = 0; index < bank_accounts.length; index++) {
          const cuenta_clabe = (bank_accounts[index].clabe || "").toString().padStart(18, "0");
          const bank_code = cuenta_clabe.substring(0, 3);
          const bank_info = await getBankNameByCode(bank_code);

          accounts.push({
            id: bank_accounts[index].id,
            bank_id: bank_info?.id || "",
            clabe: cuenta_clabe,
            bank_name: bank_info?.name || "",
            documents: bank_accounts[index].documents,
          });
        }

        setListDocuments(documents);

        if (accounts.length) {
          formik.setFieldValue("accounts", accounts);
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleAddAccount = () => {
    if (formik.values.accounts.length >= 3) {
      return;
    }
    formik.setFieldValue("accounts", [...formik.values.accounts, { ...emptyBankAccount }]);
  };

  useEffect(() => {
    if (interfaceConfig.formularios?.informacion_bancaria_segundo_aval) {
      const number_statements =
        interfaceConfig.formularios?.informacion_bancaria_segundo_aval?.numero_estados_cuenta || 1;
      setNumberOfBankStatements(number_statements);
    }
  }, [interfaceConfig]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información bancaria
          </Typography>

          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>1</strong> de 1
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Ingresa los datos de tu cuenta principal, dónde concentras la mayoría de tu actividad bancaría.
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            {formik.values.accounts.map((account: any, index: number) => (
              <Box key={index} mt={index > 0 ? 4 : 0}>
                <TextField
                  name={`accounts[${index}].clabe`}
                  label="Cuenta CLABE"
                  margin="normal"
                  type="text"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                  value={account.clabe}
                  error={
                    formik.touched.accounts?.[index]?.clabe && Boolean((formik.errors.accounts as any)?.[index]?.clabe)
                  }
                  helperText={
                    formik.touched.accounts?.[index]?.clabe
                      ? (formik.errors.accounts as any)?.[index]?.clabe ||
                        (formik.errors.accounts as any)?.[index]?.bank_id
                      : ""
                  }
                  inputProps={{ maxLength: 18 }}
                  disabled={isLoading || formik.isSubmitting}
                />

                <TextField
                  name={`accounts[${index}].bank_name`}
                  label="Banco"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={account.bank_name}
                  error={
                    formik.touched.accounts?.[index]?.bank_name &&
                    Boolean((formik.errors.accounts as any)?.[index]?.bank_name)
                  }
                  helperText={
                    formik.touched.accounts?.[index]?.bank_name
                      ? (formik.errors.accounts as any)?.[index]?.bank_name
                      : ""
                  }
                  disabled={true}
                />

                {[...Array(numberOfBankStatements)].map((_, docIdx) => {
                  const document = account.documents[docIdx] || {};
                  const inputName = `accounts[${index}].documents[${docIdx}]`;

                  const uploadedFile = listDocuments.find((item) => item.id === document.document_id);

                  // Verificar si la cuenta bancaria está completa (CLABE válida y banco identificado)
                  const isBankAccountComplete = account.clabe.length === 18 && account.bank_id && account.bank_name;

                  return (
                    <CustomUploadFileV2
                      key={docIdx}
                      mt={2}
                      inputName={`${inputName}.document_id`}
                      uploadedFile={uploadedFile || undefined}
                      setUploadedFile={(newUpload) => {
                        const updatedDocs = [...formik.values.accounts[index].documents];
                        updatedDocs[docIdx] = {
                          document_id: newUpload?.id || "",
                          document_order: docIdx + 1,
                        };
                        formik.setFieldValue(`accounts[${index}].documents`, updatedDocs);

                        if (newUpload) {
                          setListDocuments((prev) => {
                            const exists = prev.some((doc) => doc.id === newUpload.id);
                            return exists ? prev : [...prev, newUpload];
                          });
                        }
                      }}
                      optionalName={`Estado de cuenta ${docIdx + 1}`}
                      optionalDescription="Debes cargar un estado de cuenta bancario de los últimos 3 meses"
                      metadata={{
                        fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                        uploadType: "person",
                        personId: String(shareholderData?.id) as UUID,
                        clientDetailsId: String(shareholderData?.client_details_id) as UUID,
                        personType: "segundo_aval",
                      }}
                      disabled={isLoading || formik.isSubmitting || !isBankAccountComplete}
                      required
                      uploadService="second_guarantee"
                    />
                  );
                })}
              </Box>
            ))}

            <Typography
              onClick={handleAddAccount}
              color={theme?.palette?.primary?.main}
              fontSize={"15px"}
              fontWeight={400}
              mt={3}
              sx={{
                cursor: formik.values.accounts.length >= 3 ? "not-allowed" : "pointer",
              }}
            >
              + Agregar cuenta
            </Typography>

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0px", height: "100%" }}
                  onClick={handleOnClickRestart}
                  disabled={isLoading || formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="default"
                  disabled={isLoading || !formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionBancariaSegundoAval;
